export type BucketTypeOptionValue = 'INCOME' | 'BUDGET' | 'SAVINGS' | 'TRANSFERS';

/**
 * User-facing bucket type labels and field rules for add/edit budget forms.
 * Product policy: type can change after creation; existing transactions stay put.
 * See CONTEXT.md (Bucket Type, Bucket Type Change, Savings Goal).
 */
export interface BucketTypeOption {
  value: BucketTypeOptionValue;
  label: string;
  description: string;
}

export const BUCKET_TYPE_OPTIONS: readonly BucketTypeOption[] = [
  {
    value: 'BUDGET',
    label: 'Spending',
    description: 'Track everyday spending against a monthly budget.',
  },
  {
    value: 'INCOME',
    label: 'Income',
    description: 'Set expected monthly income for this budget.',
  },
  {
    value: 'SAVINGS',
    label: 'Savings',
    description: 'Save toward a goal with a monthly contribution.',
  },
  {
    value: 'TRANSFERS',
    label: 'Transfers',
    description: 'Hold account-to-account transfers without affecting spending totals.',
  },
] as const;

/**
 * Options offered in add/edit pickers. Transfers is system-managed (LCL-66):
 * every account gets exactly one, seeded server-side — users cannot create
 * more or change types to/from it (the API rejects both), so pickers must
 * not offer it. BUCKET_TYPE_OPTIONS stays complete for display/label lookups.
 */
export const SELECTABLE_BUCKET_TYPE_OPTIONS: readonly BucketTypeOption[] = BUCKET_TYPE_OPTIONS.filter(
  option => option.value !== 'TRANSFERS',
);

export function bucketTypeShowsGoal(type: BucketTypeOptionValue): boolean {
  return type === 'SAVINGS';
}

export function bucketTypeAmountLabel(type: BucketTypeOptionValue): string {
  if (type === 'INCOME') return 'Expected monthly income';
  if (type === 'SAVINGS') return 'Monthly contribution';
  if (type === 'TRANSFERS') return 'Monthly amount';
  return 'Monthly budget';
}

export function bucketTypeGoalLabel(): string {
  return 'Savings goal';
}
